import type { LucideIcon } from 'lucide-react';
import { Activity, Check, CircleAlert, Loader2, Square } from 'lucide-react';

import type {
  ConnectorInvocationStatus,
  InvocationStatusType,
} from '@/types/core/connector-invocation-status';

export type ConnectorRunStatusVariant =
  | 'neutral'
  | 'teal'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

type ConnectorRunStatusConfig = {
  label: string;
  summary: string;
  variant: ConnectorRunStatusVariant;
  pulse: boolean;
  icon: LucideIcon;
  iconSpin: boolean;
};

export const connectorRunStatusVariantBorder: Record<
  ConnectorRunStatusVariant,
  string
> = {
  neutral: 'border-border',
  teal: 'border-teal',
  info: 'border-info',
  success: 'border-success',
  warning: 'border-warning',
  error: 'border-error',
};

export const connectorRunStatusConfig: Record<
  InvocationStatusType,
  ConnectorRunStatusConfig
> = {
  queued: {
    label: 'Queued',
    summary: 'The connector run is scheduled and waiting to begin.',
    variant: 'neutral',
    pulse: true,
    icon: Activity,
    iconSpin: false,
  },
  requested: {
    label: 'Requested',
    summary: 'The connector run request has been accepted.',
    variant: 'teal',
    pulse: true,
    icon: Activity,
    iconSpin: false,
  },
  running: {
    label: 'Running',
    summary:
      'The connector subprocess is collecting payloads from the source (metrics.lifecycle.step collecting when present).',
    variant: 'info',
    pulse: true,
    icon: Loader2,
    iconSpin: true,
  },
  reconciling: {
    label: 'Reconciling',
    summary:
      'Reserved for future worker phases; current builds rarely emit this. Treat like Running and use metrics.lifecycle.step for substeps.',
    variant: 'info',
    pulse: true,
    icon: Loader2,
    iconSpin: true,
  },
  waiting: {
    label: 'Waiting',
    summary:
      'Mesh is waiting for catalog dependencies to catch up before continuing.',
    variant: 'info',
    pulse: true,
    icon: Loader2,
    iconSpin: true,
  },
  updating: {
    label: 'Updating',
    summary:
      'Mesh reconciles staged catalog data: verifying against the remote catalog (planning), then applying catalog batches. Subtitle and timeline show which substep is active.',
    variant: 'info',
    pulse: true,
    icon: Loader2,
    iconSpin: true,
  },
  completed: {
    label: 'Completed',
    summary: 'The connector run finished successfully.',
    variant: 'success',
    pulse: false,
    icon: Check,
    iconSpin: false,
  },
  failed: {
    label: 'Failed',
    summary: 'The connector run stopped with an error.',
    variant: 'error',
    pulse: false,
    icon: CircleAlert,
    iconSpin: false,
  },
  stopped: {
    label: 'Stopped',
    summary: 'The connector run was stopped by the user.',
    variant: 'warning',
    pulse: false,
    icon: Square,
    iconSpin: false,
  },
};

export function isActiveInvocationStatus(
  status: ConnectorInvocationStatus | null | undefined
) {
  return (
    status?.status === 'running' ||
    status?.status === 'reconciling' ||
    status?.status === 'waiting' ||
    status?.status === 'updating'
  );
}

export function getInvocationStatusLabel(
  status: InvocationStatusType | undefined
) {
  return status ? connectorRunStatusConfig[status].label : 'No Run';
}
